import React, { useState, useEffect } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { SelectPicker, Placeholder, Table } from "rsuite";
import Swal from "sweetalert2";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import axiosInstance from "../../../../utils/axios";
import Loader from "../../common/loader";
import moment from "moment";

const { Column, HeaderCell, Cell } = Table;

function RawDataEventsTab({ teamsData }) {
  const { t } = useTranslation();
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [selectedMatchId, setSelectedMatchId] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const loadScript = (url) => {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${url}"]`)) return resolve();
      const script = document.createElement("script");
      script.src = url;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  const handleExportPDF = async () => {
    if (!filteredEvents.length) return;
    try {
      setIsExporting(true);
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.28/jspdf.plugin.autotable.min.js");

      const { jsPDF } = window.jspdf;
      const doc = new jsPDF();

      // Header Branding
      doc.setFontSize(22);
      doc.setTextColor(32, 128, 120); // #208078
      doc.text("mNetrics", 14, 20);
      
      doc.setFontSize(14);
      doc.setTextColor(44, 42, 99); // #2c2a63
      doc.text("Raw Event Logs Report", 14, 30);

      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(`Generated: ${moment().format("MMMM Do YYYY, h:mm:ss a")}`, 14, 37);

      // Report Metadata / Filters
      const teamLabel = teamOptions.find(t => t.value === selectedTeamId)?.label || "N/A";
      const matchLabel = matchOptions.find(m => m.value === selectedMatchId)?.label || "All Matches";
      
      doc.setDrawColor(230);
      doc.line(14, 42, 196, 42);

      doc.setFontSize(10);
      doc.setTextColor(50);
      doc.text(`Team: ${teamLabel}`, 14, 50);
      doc.text(`Match Context: ${matchLabel}`, 14, 56);
      if (selectedPlayerId) {
        const playerLabel = playerOptions.find(p => p.value === selectedPlayerId)?.label || "N/A";
        doc.text(`Player Filter: ${playerLabel}`, 14, 62);
      }

      // tableColumns matching the UI table
      const SUB_ACTIONS = ["TECHNICAL_SUBSTITUTION", "SUBSTITUTION", "TECHNICAL-SUBSTITUTION", "CHANGE_PLAYER_POSITION", "CHANGE-PLAYER-POSITION"];
      const tableColumn = ["Q", "Time", "Player", "Team", "Position", "Action", "Pts"];
      const tableRows = filteredEvents.map(e => {
        const act = (e.actionType || "").toUpperCase();
        const isSub = SUB_ACTIONS.includes(act);
        return [
          `Q${e.quarter}`,
          moment(e.createdAt).format("HH:mm:ss"),
          e.playerName || "-",
          e.teamName || "-",
          e.position || "-",
          (e.actionType || "").replace(/-/g, " "),
          isSub ? 0 : (e.points || 0)
        ];
      });

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: selectedPlayerId ? 68 : 62,
        theme: 'striped',
        headStyles: { fillColor: [32, 128, 120], fontSize: 9, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 2.5 },
        alternateRowStyles: { fillColor: [248, 252, 251] },
        margin: { top: 20 },
      });

      const fileName = `mNetrics_Events_${teamLabel.split(' ')[0]}_${moment().format("YYYYMMDD")}.pdf`;
      doc.save(fileName);
      
    } catch (error) {
      console.error("PDF Export Error:", error);
      Swal.fire({
        icon: "error",
        text: "Failed to load PDF library or generate report. Check your connection.",
        ...SWAL_SETTINGS,
      });
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (teamsData?.length > 0 && !selectedTeamId) {
      const firstTeamId = teamsData[0].teamId || teamsData[0].id;
      setSelectedTeamId(firstTeamId);
    }
  }, [teamsData, selectedTeamId]);

  // Fetch available matches for the selected team to populate the dropdown
  const { data: insightsData } = useQuery({
    queryKey: ["team-insights-for-matches", selectedTeamId],
    queryFn: async () => {
      const res = await axiosInstance({ role: "super_admin" }).get(
        `admin/reports/team-insights?teamId=${selectedTeamId}`
      );
      return res;
    },
    enabled: !!selectedTeamId,
    refetchOnWindowFocus: false,
  });

  const { data: rawEventsData, isLoading } = useQuery({
    queryKey: ["raw-events", selectedTeamId, selectedMatchId],
    queryFn: async () => {
      const url = `admin/reports/raw-events?teamId=${selectedTeamId}${
        selectedMatchId ? `&matchId=${selectedMatchId}` : ""
      }`;
      const res = await axiosInstance({ role: "super_admin" }).get(url);
      return res;
    },
    enabled: !!selectedTeamId,
    refetchOnWindowFocus: false,
    onError: (error) => {
      Swal.fire({
        icon: "error",
        text: error?.response?.data?.message || "Failed to load raw events",
        ...SWAL_SETTINGS,
      });
    },
  });

  const [selectedPlayerId, setSelectedPlayerId] = useState("");

const safeTeams = Array.isArray(teamsData?.list)
  ? teamsData.list
  : [];

const teamOptions = safeTeams.map((t) => ({
  label: `${t.teamName || "Unknown"} (${t.clubAdminName || "Unknown"})`,
  value: t.teamId || t.id,
}));

  const matchOptions = [
    { label: "All Matches", value: "" },
    ...(insightsData?.data?.matches || []).map((m) => ({
      label: m.title
        ? `${m.title} (${m.homeTeamName} vs ${m.awayTeamName}) — ${m.score}`
        : `${m.homeTeamName} vs ${m.awayTeamName} on ${m.date}`,
      value: m.id,
    })),
  ];

  const events = rawEventsData?.data || [];

  // New Player Filter Options
  const playerOptions = [
    { label: "All Players", value: "" },
    ...[...new Set(events?.map(e => e.playerId))].filter(Boolean).map(id => {
        const event = events.find(e => e.playerId === id);
        return { label: event?.playerName || id, value: id };
    })
  ].sort((a, b) => a.label.localeCompare(b.label));

  const weights = insightsData?.data?.weights;
  
  const defInterception = weights?.DEFENDERS?.INTERCEPTION !== undefined ? Number(weights.DEFENDERS.INTERCEPTION) : 1.5;
  const defTip = weights?.DEFENDERS?.TIP !== undefined ? Number(weights.DEFENDERS.TIP) : 0.75;
  const defRebound = weights?.DEFENDERS?.DEFENSIVE_REBOUND !== undefined ? Number(weights.DEFENDERS.DEFENSIVE_REBOUND) : 0.75;

  const defInterceptionScore = (defInterception * 2);
  const defTipScore = (defTip * 2);
  const defReboundScore = (defRebound * 2);

  const goalWeight = weights?.MIDCOURT?.GOAL !== undefined ? Math.abs(Number(weights.MIDCOURT.GOAL)) : 1;
  const interceptionWeight = weights?.DEFENDERS?.INTERCEPTION !== undefined ? Math.abs(Number(weights.DEFENDERS.INTERCEPTION)) : 1.5;
  const turnoverWeight = weights?.MIDCOURT?.TURNOVER_LOST !== undefined ? Math.abs(Number(weights.MIDCOURT.TURNOVER_LOST)) : 1;

  const filteredEvents = selectedPlayerId 
    ? events.filter(e => e.playerId === selectedPlayerId)
    : events;

  return (
    <div className="raw-data-container">
      <style>{`
        .raw-data-container { padding: 20px; background: #fff; min-height: 600px; }
        .filter-section { display: flex; gap: 20px; margin-bottom: 30px; align-items: flex-end; flex-wrap: wrap; }
        .filter-item { width: 300px; }
        .filter-item label { font-size: 12px; font-weight: 700; color: #666; margin-bottom: 5px; display: block; text-transform: uppercase; }
        .calculation-basis { background: #f8f9fa; padding: 20px; border-radius: 8px; margin-top: 30px; border-left: 4px solid #208078; }
        .calculation-basis h5 { color: #208078; margin-bottom: 15px; font-weight: 700; }
        .calculation-basis ul { padding-left: 20px; }
        .calculation-basis li { margin-bottom: 8px; font-size: 14px; color: #444; }
        .calculation-basis strong { color: #2c2a63; }
        .btn-export-pdf { background: #208078; color: #fff; border: none; padding: 8px 18px; border-radius: 6px; font-size: 13px; font-weight: 700; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; box-shadow: 0 2px 6px rgba(32, 128, 120, 0.15); }
        .btn-export-pdf:hover:not(:disabled) { background: #1a6660; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(32, 128, 120, 0.25); }
        .btn-export-pdf:active:not(:disabled) { transform: translateY(0); }
        .btn-export-pdf:disabled { background: #a8cfcc; cursor: not-allowed; opacity: 0.8; }
      `}</style>

      <div className="filter-section">
        <div className="filter-item">
          <label>Select Team</label>
          <SelectPicker
            data={teamOptions}
            value={selectedTeamId}
            onChange={(val) => {
              setSelectedTeamId(val);
              setSelectedMatchId("");
              setSelectedPlayerId("");
            }}
            placeholder="Select Team"
            block
            cleanable={false}
          />
        </div>
        <div className="filter-item">
          <label>Select Match (Optional)</label>
          <SelectPicker
            data={matchOptions}
            value={selectedMatchId}
            onChange={(val) => {
                setSelectedMatchId(val);
                setSelectedPlayerId("");
            }}
            placeholder="All Matches"
            block
            cleanable={false}
            disabled={!selectedTeamId}
          />
        </div>
        <div className="filter-item">
          <label>Select Player (Optional)</label>
          <SelectPicker
            data={playerOptions}
            value={selectedPlayerId}
            onChange={setSelectedPlayerId}
            placeholder="All Players"
            block
            cleanable={false}
            disabled={!events.length}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
          <Loader />
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 style={{ fontWeight: 700, color: "#2c2a63", margin: 0 }}>Raw Event Logs</h6>
            <button 
                className="btn-export-pdf" 
                onClick={handleExportPDF}
                disabled={isExporting || !filteredEvents.length}
            >
                {isExporting ? "Generating PDF..." : "Export PDF"}
                {!isExporting && <i className="ri-file-pdf-line ms-2" style={{ fontSize: '16px' }}></i>}
            </button>
          </div>

          <Table
            height={400}
            data={filteredEvents}
            bordered
            cellBordered
            headerHeight={45}
            rowHeight={45}
          >
            <Column width={100} align="center">
              <HeaderCell>Quarter</HeaderCell>
              <Cell dataKey="quarter" />
            </Column>

            <Column width={150}>
              <HeaderCell>Time</HeaderCell>
              <Cell>
                {(rowData) => moment(rowData.createdAt).format("HH:mm:ss")}
              </Cell>
            </Column>

            <Column width={200} flexGrow={1}>
              <HeaderCell>Player</HeaderCell>
              <Cell dataKey="playerName" />
            </Column>

            <Column width={150}>
              <HeaderCell>Team</HeaderCell>
              <Cell dataKey="teamName" />
            </Column>

            <Column width={120} align="center">
              <HeaderCell>Position</HeaderCell>
              <Cell dataKey="position" />
            </Column>

            <Column width={180}>
              <HeaderCell>Action Type</HeaderCell>
              <Cell dataKey="actionType" />
            </Column>

            <Column width={100} align="center">
              <HeaderCell>Points</HeaderCell>
              <Cell>
                {(rowData) => {
                  const act = (rowData.actionType || "").toUpperCase();
                  const isSub = ["TECHNICAL_SUBSTITUTION", "SUBSTITUTION", "TECHNICAL-SUBSTITUTION", "CHANGE_PLAYER_POSITION", "CHANGE-PLAYER-POSITION"].includes(act);
                  return isSub ? 0 : (rowData.points || 0);
                }}
              </Cell>
            </Column>

            <Column width={200}>
                <HeaderCell>Match</HeaderCell>
                <Cell dataKey="matchName" />
            </Column>
          </Table>

          {/* <div className="calculation-basis">
            <h5>How Insights are Calculated</h5>
            <ul>
                <li><strong>Shooting Efficiency:</strong> Calculated as <code>(Total Goals / Total Shots) * 100</code>. Only actions of type <code>GOAL</code> and <code>SHOT</code> are considered.</li>
                <li><strong>Defensive Impact:</strong> Weighted sum of defensive actions: <code>(Interceptions * {defInterceptionScore}) + (Blocks/Tips * {defTipScore}) + (Rebounds * {defReboundScore})</code>.</li>
                <li><strong>Safe Play %:</strong> Measure of ball security: <code>(1 - (Turnovers / Total Possessions)) * 100</code>. Turnovers include <code>TURNOVER</code>, <code>HELD_BALL</code>, and <code>SELF_ERROR</code>.</li>
                <li><strong>Momentum Tracking:</strong> A dynamic score that increases with <code>GOALS</code> (+{goalWeight}) and <code>INTERCEPTIONS</code> (+{interceptionWeight}), and decreases with <code>GOAL_AGAINST</code> (-{goalWeight}) and <code>TURNOVERS</code> (-{turnoverWeight}).</li>
                <li><strong>Conversion Chains:</strong> Tracks outcomes starting from a <code>CENTER_PASS</code>. A successful chain ends in a <code>GOAL</code> without lost possession.</li>
                <li><strong>Quarterly Reports:</strong> Aggregates all actions by the <code>quarter</code> field recorded at the time of the event.</li>
            </ul>
          </div> */}
        </>
      )}
    </div>
  );
}

export default RawDataEventsTab;
